import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  domError,
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
  withTimeout,
} from './shared.ts';

async function webSocketEcho(): Promise<RawData> {
  return await withTimeout(
    new Promise<RawData>((resolve) => {
      const scheme = location.protocol == 'https:' ? 'wss:' : 'ws:';
      const socket = new WebSocket(`${scheme}//${location.host}/echo.ws`);
      socket.addEventListener('open', () => socket.send('abc-test-ping'));
      socket.addEventListener('message', (event) => {
        const message = String(event.data);
        socket.close();
        resolve({ message });
      });
      socket.addEventListener('error', (event) => {
        resolve({ errorType: event.type });
      });
    }),
    5000,
    'WebSocket echo timed out',
  );
}

async function fetchOutcome(url: string): Promise<RawData> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(url, {
      cache: 'no-store',
      signal: controller.signal,
    });
    return {
      status: response.status,
      statusText: response.statusText,
      responseType: response.type,
      redirected: response.redirected,
    };
  } catch (error) {
    return {
      errorName: controller.signal.aborted
        ? 'TimeoutError'
        : domError(error).name,
      errorMessage: domError(error).message,
    };
  } finally {
    window.clearTimeout(timeout);
  }
}

async function collect(): Promise<RawData> {
  const sameOriginHttps = await fetchOutcome('/network.json');
  const externalHttps = await fetchOutcome('https://ip.betandcalm.com/');
  let websocket: RawData;
  try {
    websocket = await webSocketEcho();
  } catch (error) {
    websocket = {
      errorName: domError(error).name,
      errorMessage: domError(error).message,
    };
  }
  return {
    sameOriginHttps,
    externalHttps,
    websocket,
  };
}

function validateProxyFunctionality(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  for (const [key, label] of [
    ['sameOriginHttps', 'Same-origin HTTPS'],
    ['externalHttps', 'External HTTPS'],
  ] as const) {
    const response = isRecord(rawData[key]) ? rawData[key] : {};
    if (
      typeof response.status != 'number' ||
      response.status < 200 ||
      response.status >= 400
    ) {
      reasons.push(`${label} request failed`);
    }
  }
  if (
    !isRecord(rawData.websocket) ||
    !String(rawData.websocket.message ?? '').includes('abc-test-ping')
  ) {
    reasons.push('Secure WebSocket echo failed');
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return isRecord(rawData.sameOriginHttps) &&
    Number(rawData.sameOriginHttps.status) >= 200 &&
    isRecord(rawData.externalHttps) &&
    Number(rawData.externalHttps.status) >= 200
    ? 'HTTPS/WebSocket works'
    : 'Network failure';
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateProxyFunctionality(rawData);
}

export const proxyFunctionalityTest: TestDefinition = {
  id: 'proxyFunctionality',
  title: 'Proxy functionality',
  category: 'shared',
  format,
  run,
  validate,
};
